import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import findIndex from 'lodash/findIndex';
import { CHAT_LIST_PAGE_SIZE } from 'src/content/Chat/utils';
import type { IAiModel, StringKeys } from 'src/types/base';
import type { ChatData } from 'src/types/chat';
import type { PdfDocument } from 'src/types/document';
import type { RESOURCE_TYPE } from 'src/types/enum';
import { CHAT_CLASS, CHAT_SOURCE_TYPES } from 'src/types/enum';
import { v4 as uuidV4 } from 'uuid';

export interface StreamEventTimings {
  querySendTime?: number;
  queryAcknowledgeTime?: number;
  responseStreamStartTime?: number;
  responseStreamEndTime?: number;
}

export interface CoolDownModelInfo {
  modelId: string;
  availableAt: string;
  availableTime: Date;
  modelTier: string;
}

export interface ChatContext {
  tags: any[];
  openedDocuments: PdfDocument[];
  isDocumentViewerOpen: boolean;
  isCanvasChat: boolean;
  selectedDocumentIndex: number;
  streamEventTimings: StreamEventTimings;
  chat_type: CHAT_SOURCE_TYPES;
  knowledge_type: CHAT_SOURCE_TYPES;
  chatModel: IAiModel;
  messageId?: number;
  documentContext?: {
    id: number;
    name: string;
    workspaceName?: string;
    assistant_id?: number;
    type?: RESOURCE_TYPE;
  };
  shareChatData?: {
    link: string;
    sharedOn: string;
  };
  web_search_enabled?: boolean;
  pro_search_enabled?: boolean;
  image_generation_enabled?: boolean;
  chat_class?: CHAT_CLASS;
  rid?: number;
  ts?: string;
  chatFiles?: any[];
  noOfChatAttachments?: number;
  hasImageAttachments?: boolean;
}

const initialState: {
  selectedChat: number;
  chatCreationError: boolean;
  isParameterModalOpen: boolean;
  chats: ChatData[];
  chatContexts: {
    [id: number]: ChatContext;
  };
  chatSourceType: CHAT_SOURCE_TYPES;
  currentlyDownloadingDocuments: number[];
  chatsPageNumber: number;
  showChatHistoryPanel: boolean;
  coolDownModels: Record<string, Omit<CoolDownModelInfo, 'modelId'>>;
} = {
  chats: [],
  selectedChat: undefined,
  chatCreationError: false,
  isParameterModalOpen: false,
  chatContexts: {},
  currentlyDownloadingDocuments: [],
  chatsPageNumber: 0,
  showChatHistoryPanel: false,
  coolDownModels: {},
  chatSourceType: CHAT_SOURCE_TYPES.ChatGPT
};

export const defaultChatContext = {
  tags: [],
  openedDocuments: [],
  isDocumentViewerOpen: false,
  isCanvasChat: false,
  selectedDocumentIndex: 0,
  streamEventTimings: {},
  chat_type: CHAT_SOURCE_TYPES.ChatGPT,
  knowledge_type: CHAT_SOURCE_TYPES.EmpowerGPT,
  chatModel: null,
  web_search_enabled: false,
  chat_class: CHAT_CLASS?.NORMAL,
  chatFiles: [],
  noOfChatAttachments: 0,
  hasImageAttachments: false
};

const ChatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChats(state, { payload }) {
      state.chats = payload;
    },
    updateChatData(
      state,
      action: PayloadAction<{ chatId: number; chatData: any }>
    ) {
      const { chatId, chatData } = action.payload;
      const chatIndex = findIndex(state.chats, { id: chatId });
      if (chatIndex > -1) {
        state.chats[chatIndex] = { ...state.chats[chatIndex], ...chatData };
      }
    },
    appendChat(state, action: PayloadAction<ChatData>) {
      if (
        state.chats.length >
        CHAT_LIST_PAGE_SIZE * (state.chatsPageNumber + 1)
      ) {
        state.chats.splice(state.chats.length - 1, 1);
      }
      state.chats.unshift(action.payload);
    },
    setSelectedChat(state, action) {
      state.selectedChat = action.payload;
    },
    openParameterModal(state) {
      state.isParameterModalOpen = true;
    },
    closeParameterModal(state) {
      state.isParameterModalOpen = false;
    },
    createChatSnapshot(
      state,
      action: PayloadAction<{ chatId: number; chatContext?: ChatContext }>
    ) {
      const { chatId, chatContext } = action.payload;
      const selectedChatContext = state.chatContexts[chatId];

      if (!chatContext) {
        if (!selectedChatContext) {
          state.chatContexts = {
            ...state.chatContexts,
            [chatId]: { ...defaultChatContext }
          };
        }
        return;
      }

      const hasDocumentContextOverride = Object.prototype.hasOwnProperty.call(
        chatContext,
        'documentContext'
      );

      state.chatContexts = {
        ...state.chatContexts,
        [chatId]: {
          ...defaultChatContext,
          ...(selectedChatContext || {}),
          ...chatContext,
          ...(hasDocumentContextOverride
            ? { documentContext: chatContext.documentContext }
            : selectedChatContext?.documentContext
            ? { documentContext: selectedChatContext.documentContext }
            : {})
        }
      };
    },
    updateChatTags(state, { payload }) {
      if (state.chatContexts[payload.chatId]) {
        state.chatContexts[payload.chatId].tags = payload.tags;
      }
    },
    updateChatModel(
      state,
      action: PayloadAction<{ chatId: number; chatModel: IAiModel }>
    ) {
      const { chatId = 0, chatModel } = action.payload;
      const chat = state.chatContexts[chatId];
      if (!chat) {
        return;
      }
      state.chatContexts[chatId].chatModel = chatModel;
    },
    updateChatWebSearch(
      state,
      action: PayloadAction<{ chatId: number; isWebSearch: boolean }>
    ) {
      const { chatId, isWebSearch } = action.payload;
      state.chatContexts[chatId].web_search_enabled = isWebSearch;
    },
    clearChatSnapshot(state, { payload }: PayloadAction<number>) {
      if (state.chatContexts[payload]) {
        delete state.chatContexts[payload];
      }
    },
    updateChatSourceType(state, { payload }) {
      if (!state.chatContexts[0]) {
        state.chatContexts[0] = { ...defaultChatContext };
      }
      state.chatContexts[0].chat_type = payload;
      state.chatContexts[0].knowledge_type = payload;
      state.chatSourceType = payload;
    },
    updateEnterpriseKnowledgeType(
      state,
      action: PayloadAction<{
        chatId: number;
        knowledgeType: CHAT_SOURCE_TYPES;
      }>
    ) {
      const { chatId, knowledgeType } = action.payload;

      if (state.chatContexts[chatId]) {
        state.chatContexts = {
          ...state.chatContexts,
          [chatId]: {
            ...state.chatContexts[chatId],
            knowledge_type: knowledgeType,
            chat_type: knowledgeType
          }
        };
        // chat_type/chatSourceType are read as "the" current type by
        // useIsEmpowerGptChat, getSendMsgAPIParams, and the Compare page's
        // useChatGlobalContext, so they must stay in sync with the value the
        // switcher shows as selected (knowledge_type), not just the display copy.
        state.chatSourceType = knowledgeType;
      }
    },
    updateOpenedDocument(
      state,
      action: PayloadAction<{
        chatId: number;
        document: {
          pageNumber: number;
          name: string;
          id: number;
          index: number;
        };
      }>
    ) {
      const { payload } = action;
      if (
        state.chatContexts[payload.chatId].openedDocuments[
          payload.document.index
        ].pageNumber !== payload.document.pageNumber
      ) {
        state.chatContexts[payload.chatId].openedDocuments[
          payload.document.index
        ] = {
          ...state.chatContexts[payload.chatId].openedDocuments[
            payload.document.index
          ],
          ...payload.document
        };
      }
      state.chatContexts[payload.chatId].isDocumentViewerOpen = true;
      if (
        state.chatContexts[payload.chatId].selectedDocumentIndex !==
        payload.document.index
      ) {
        state.chatContexts[payload.chatId].selectedDocumentIndex =
          payload.document.index;
      }
    },
    addOpenedDocument(
      state,
      action: PayloadAction<{
        messageId?: number;
        chatId: number;
        document: PdfDocument;
      }>
    ) {
      const { payload } = action;
      const uuid = uuidV4();

      state.chatContexts[payload.chatId].openedDocuments.unshift({
        ...payload.document,
        uuid: uuid
      });
      state.chatContexts[payload.chatId].isDocumentViewerOpen = true;
      state.chatContexts[payload.chatId].messageId = payload.messageId;
    },
    removeOpenedDocument(
      state,
      action: PayloadAction<{ chatId: number; documentIndex: number }>
    ) {
      const { chatId, documentIndex } = action.payload;
      state.chatContexts[chatId].openedDocuments.splice(documentIndex, 1);
      if (!state.chatContexts[chatId].openedDocuments.length) {
        state.chatContexts[chatId].isDocumentViewerOpen = false;
      }
    },
    openDocumentViewer(state, { payload }: PayloadAction<number>) {
      state.chatContexts[payload].isDocumentViewerOpen = true;
    },
    closeDocumentViewer(state, { payload }: PayloadAction<number>) {
      if (payload in state.chatContexts) {
        state.chatContexts[payload].isDocumentViewerOpen = false;
        state.chatContexts[payload].openedDocuments = [];
      }
    },
    addCurrentlyDownloadingDocuments(state, action: PayloadAction<number>) {
      state.currentlyDownloadingDocuments.push(action.payload);
    },
    removeCurrentlyDownloadingDocuments(state, action: PayloadAction<number>) {
      const { payload } = action;
      state.currentlyDownloadingDocuments =
        state.currentlyDownloadingDocuments.filter((item) => item !== payload);
    },
    updateStreamEventTimings(
      state,
      action: PayloadAction<{
        chatId: number;
        streamEventTimings: StreamEventTimings;
      }>
    ) {
      const { chatId, streamEventTimings } = action.payload;
      if (state.chatContexts[chatId]) {
        state.chatContexts = {
          ...state.chatContexts,
          [chatId]: {
            ...state.chatContexts[chatId],
            streamEventTimings: { ...streamEventTimings }
          }
        };
      }
    },
    createShareChat(
      state,
      action: PayloadAction<{
        chatId: number;
        shareChatData: { link: string; sharedOn: string };
      }>
    ) {
      const { chatId, shareChatData } = action.payload;
      if (state.chatContexts[chatId]) {
        state.chatContexts = {
          ...state.chatContexts,
          [chatId]: {
            ...state.chatContexts[chatId],
            shareChatData: { ...shareChatData }
          }
        };
      }
    },
    updateChatsPageNumber(state, { payload }) {
      state.chatsPageNumber = payload || 0;
    },
    toggleChatHistoryPan(state, { payload }) {
      state.showChatHistoryPanel = payload;
    },
    updateChatAttachmentsCount(
      state,
      action: PayloadAction<{
        chatId: number;
        chatAttachmentsCount: number;
        hasImageAttachments?: boolean;
      }>
    ) {
      const {
        chatId,
        chatAttachmentsCount,
        hasImageAttachments = false
      } = action.payload;
      state.chatContexts[chatId].noOfChatAttachments = chatAttachmentsCount;
      state.chatContexts[chatId].hasImageAttachments = hasImageAttachments;
    },
    updateChatAttachments(
      state,
      action: PayloadAction<{ chatId: number; chatFiles: StringKeys[] }>
    ) {
      const { chatId, chatFiles } = action.payload;
      state.chatContexts[chatId].chatFiles = chatFiles;
    },
    setChatCreationError(state, action: PayloadAction<{ isError: boolean }>) {
      const { isError } = action.payload;
      state.chatCreationError = isError;
    },
    setCoolDownModels(
      state,
      action: PayloadAction<{
        coolDownModels: Record<string, Omit<CoolDownModelInfo, 'modelId'>> | {};
      }>
    ) {
      state.coolDownModels = action.payload.coolDownModels;
    },
    addCoolDownModel(state, action: PayloadAction<CoolDownModelInfo>) {
      const { modelId, availableAt, availableTime, modelTier } = action.payload;
      state.coolDownModels[modelId] = { availableAt, availableTime, modelTier };
    },
    removeCoolDownModel(state, action: PayloadAction<number>) {
      const model_id = action.payload;
      delete state.coolDownModels[model_id];
    },
    updateIsCanvasChat(
      state,
      action: PayloadAction<{ chatId: number; isCanvasChat: boolean }>
    ) {
      const { chatId, isCanvasChat } = action.payload;
      if (state.chatContexts[chatId]) {
        state.chatContexts[chatId].isCanvasChat = isCanvasChat;
      }
    }
  }
});

export default ChatSlice;

export const reducer = ChatSlice.reducer;

export const {
  setChats,
  updateChatData,
  appendChat,
  setSelectedChat,
  openParameterModal,
  closeParameterModal,
  createChatSnapshot,
  updateChatTags,
  updateChatModel,
  updateChatWebSearch,
  clearChatSnapshot,
  addOpenedDocument,
  updateOpenedDocument,
  removeOpenedDocument,
  openDocumentViewer,
  closeDocumentViewer,
  addCurrentlyDownloadingDocuments,
  removeCurrentlyDownloadingDocuments,
  updateStreamEventTimings,
  updateChatSourceType,
  updateEnterpriseKnowledgeType,
  createShareChat,
  updateChatsPageNumber,
  toggleChatHistoryPan,
  updateChatAttachmentsCount,
  updateChatAttachments,
  setChatCreationError,
  addCoolDownModel,
  removeCoolDownModel,
  setCoolDownModels,
  updateIsCanvasChat
} = ChatSlice.actions;
